import { useEffect, useState, type ReactNode } from 'react';

interface AuthImageProps {
  /** Same-origin app URL, e.g. /api/photos/:id/thumb */
  src: string;
  alt?: string;
  className?: string;
  onClick?: () => void;
  /** Rendered if the fetch fails (e.g. 403 for non-admins on full-size). */
  fallback?: ReactNode;
}

/**
 * Loads an authenticated image: the bearer token lives in localStorage, so a
 * plain <img src> can't be used for protected endpoints. This fetches the bytes
 * with the Authorization header and renders them via an object URL.
 */
export function AuthImage({ src, alt, className, onClick, fallback = null }: AuthImageProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);

    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers: HeadersInit = {};
        if (token && token !== 'null') headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(src, { headers, credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (active) setUrl(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      } catch {
        if (active) setFailed(true);
      }
    })();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (failed) return <>{fallback}</>;
  if (!url) return <div className={className} aria-busy="true" />;
  return <img src={url} alt={alt || ''} className={className} onClick={onClick} />;
}
